import React, { useEffect, useState } from 'react';
import { ContentCopyRounded, SettingsBackupRestoreRounded } from '@mui/icons-material';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material';
import { useNotification, useRequest } from 'shared/hooks';
import { AUTH_METHODS } from 'shared/constants/building';
import { switchConfig } from 'shared/api/networking';
import { copyValueToClipboard } from 'shared/utils';
import { VARIANTS } from 'shared/constants';
import { NumberEditor, TextAreaEditor, TextEditor } from 'shared/components/data-display/editors';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { qosEntity } from 'shared/entityDetails';
import { getQos } from 'shared/api/qos';
import SplitButton from 'shared/components/SplitButton';
import { withAsyncSelect } from 'shared/components/data-display/withSelectors';

const resultsDescriptions = [
  {
    key: 'results',
    label: 'Configuration',
    editor: TextAreaEditor,
  },
];

const overrideDescriptions = [
  {
    key: 'name',
    label: 'Building Name',
    editor: TextEditor,
  },
  {
    key: 'qos',
    formatter: (b) => b.qos.description,
    label: 'Max Speed',
    editor: withAsyncSelect({
      ...qosEntity,
      label: 'Max Speed',
    }),
    editKey: 'qos_id',
  },
  {
    key: 'vlan_range_start',
    label: 'VLAN Start',
    editor: NumberEditor,
  },
  {
    key: 'vlan_range_end',
    label: 'VLAN End',
    editor: NumberEditor,
  },
];

/**
 * @param {Object} props
 * @param {Object<string, *>} props.building - Building object with configuration details
 */
function ConfigScript(props) {
  const {
    building,
    building: {
      auth_method: authMethod,
      qos,
      qos_id: qosId,
      units,
      vlan_range_start: vlanRangeStart,
      vlan_range_end: vlanRangeEnd,
      name,
    },
  } = props;

  const { request } = useRequest();

  const {
    pushError,
    pushInfo,
  } = useNotification();
  const [overrideValues, setOverrideValues] = useState({
    name,
    vlan_range_start: vlanRangeStart,
    maxLimitSpeed: qos.description,
    qos,
    qos_id: qosId,
    vlan_range_end: vlanRangeEnd,
  });

  const [overrideShow, setOverrideShow] = useState(false);
  const handleOverrideClose = () => setOverrideShow(false);
  const handleOverrideShow = () => setOverrideShow(true);

  const [results, setResults] = useState(undefined);
  const [resultsShow, setResultsShow] = useState(false);
  const handleResultsClose = () => setResultsShow(false);
  const handleResultsShow = () => setResultsShow(true);
  const [resultsOverride, setResultsOverride] = useState(undefined);

  useEffect(() => {
    setOverrideValues({
      name,
      max_limit_speed: qos.description,
      qos,
      qos_id: qosId,
      vlan_range_start: vlanRangeStart,
      vlan_range_end: vlanRangeEnd,
    });
  }, [name, qos, qosId, vlanRangeStart, vlanRangeEnd]);

  async function config(override) {
    let response;
    if (!override) {
      if (!(name && units?.length > 0 && qos.description && vlanRangeStart && vlanRangeEnd)) {
        pushError('Invalid value. Check entries and try again');
        return;
      }
      response = await request(switchConfig({
        name,
        units,
        maxLimitSpeed: qos.description,
        vlanRangeStart,
        vlanRangeEnd,
      }));
    } else {
      handleOverrideClose();
      response = await request(switchConfig({
        name: overrideValues.name,
        units,
        maxLimitSpeed: overrideValues.max_limit_speed,
        vlanRangeStart: overrideValues.vlan_range_start,
        vlanRangeEnd: overrideValues.vlan_range_end,
      }));

      setOverrideValues({
        name,
        max_limit_speed: qos.description,
        qos,
        qos_id: qosId,
        vlan_range_start: vlanRangeStart,
        vlan_range_end: vlanRangeEnd,
      });
    }
    if (response) {
      setResults({ results: response });
      setResultsOverride({ results: response });
      handleResultsShow();
    }
  }

  const updateOverrides = (key, value) => {
    async function checkMaxSpeedChange() {
      const response = await request(getQos(value));
      if (response) {
        setOverrideValues({
          ...overrideValues,
          max_limit_speed: response.description,
          qos_id: value,
          qos: { ...response },
        });
      } else {
        pushError('failed to set Max Speed override, please try again');
      }
    }

    setOverrideValues({
      ...overrideValues,
      [key]: value,
    });
    if (key === 'qos_id' && value) {
      checkMaxSpeedChange();
    }
  };

  const create = () => config(false);
  const createWithOverride = () => config(true);

  const copyToClipboard = async () => {
    const s = resultsOverride.results;
    await copyValueToClipboard(s);
    pushInfo('Copied config to clipboard');
  };
  const revertChanges = () => setResultsOverride(results);

  return (
    <>
      <SplitButton
        disabled={!building
          || (authMethod !== AUTH_METHODS.dot1x && authMethod !== AUTH_METHODS.ipv6)}
        title="Generate Config"
        onClick={create}
        menuItems={[{
          menuItemClick: handleOverrideShow,
          menuItemTitle: 'Set Overrides',
        }]}
      />
      <Dialog open={overrideShow}>
        <DialogTitle>
          Override VLAN Ranges
        </DialogTitle>
        <DialogContent>
          <DataFormatter
            data={building}
            descriptions={overrideDescriptions}
            editing
            updatedData={overrideValues}
            updateValue={updateOverrides}
          />
        </DialogContent>
        <DialogActions>
          <Button variant={VARIANTS.outlined} onClick={handleOverrideClose}>Close</Button>
          <Button
            sx={{ marginLeft: 1 }}
            variant={VARIANTS.contained}
            onClick={createWithOverride}
          >
            Config
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={resultsShow}>
        <DialogTitle>
          {name}
        </DialogTitle>
        <DialogContent>
          <DataFormatter
            className="config-script"
            data={results}
            descriptions={resultsDescriptions}
            updatedData={resultsOverride}
            updateValue={(key, value) => setResultsOverride({
              ...resultsOverride,
              [key]: value,
            })}
            editing
          />
        </DialogContent>
        <DialogActions>
          <Button variant={VARIANTS.outlined} onClick={handleResultsClose}>Close</Button>
          <Button
            disabled={resultsOverride?.results === results?.results}
            title="Revert Changes"
            onClick={revertChanges}
          >
            <SettingsBackupRestoreRounded />
          </Button>
          <Button title="Copy to clipboard" onClick={copyToClipboard}><ContentCopyRounded /></Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ConfigScript;
